import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaRegCalendarAlt } from 'react-icons/fa';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 6px 38px 6px 14px;
  border: 1px solid #c5c5c5;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
  background: #fff;
  outline: none;
  transition: border-color .2s, box-shadow .2s;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    border-color: #008080;
    box-shadow: 0 0 0 2px rgba(0,128,128,0.15);
  }

  &::placeholder {
    color: #999;
  }

  @media (max-width: 560px) {
    font-size: clamp(14px, 3.5vw, 16px);
    padding: 10px 42px 10px 16px;
  }
`;

const CalendarIcon = styled(FaRegCalendarAlt)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #008080;
  font-size: 16px;
  cursor: pointer;
  z-index: 1;

  @media (max-width: 560px) {
    right: 16px;
    font-size: 18px;
  }
`;

const CalendarPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: ${props => props.$right ? 'auto' : '0'};
  right: ${props => props.$right ? '0' : 'auto'};
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 16px;
  z-index: 1000;
  width: 280px;
  display: ${props => props.$show ? 'block' : 'none'};

  @media (max-width: 560px) {
    left: ${props => props.$right ? 'auto' : '50%'};
    right: ${props => props.$right ? '0' : 'auto'};
    transform: ${props => props.$right ? 'none' : 'translateX(-50%)'};
    width: 260px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const MonthYearText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  font-family: 'Roboto', sans-serif;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: #008080;
  font-size: 20px;
  cursor: pointer;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 4px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #666;
  padding: 4px 0;
  font-family: 'Roboto', sans-serif;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${props => props.$selected ? '#008080' : props.$today ? 'transparent' : 'transparent'};
  color: ${props => props.$selected ? '#fff' : props.$otherMonth ? '#ccc' : '#333'};
  border: ${props => props.$today && !props.$selected ? '1px solid #008080' : 'none'};
  border-radius: 50%;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
  font-weight: ${props => props.$selected ? '500' : '400'};
  transition: background 0.2s, transform 0.1s;
  opacity: ${props => props.$disabled ? '0.3' : '1'};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${props => props.$selected ? '#006666' : 'rgba(0, 128, 128, 0.1)'};
    transform: scale(1.05);
  }

  &:disabled {
    pointer-events: none;
  }
`;

const formatDateForDisplay = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const weekDays = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

const ResponsiveDatePicker = ({
  value,
  onChange,
  minDate,
  diasSemanaPermitidos,
  placeholderText = 'DD/MM/AAAA',
  disabled = false,
  customInput,
  className,
  popoverRight,
  ...props
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = value ? new Date(value) : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const containerRef = useRef(null);

  // Sincroniza o mês exibido quando o valor é alterado externamente
  useEffect(() => {
    if (value) {
      const date = value instanceof Date ? value : new Date(value);
      if (!isNaN(date.getTime())) {
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];

    // Dias do mês anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStart - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        date: new Date(year, month - 1, prevMonthLastDay - i),
        otherMonth: true
      });
    }

    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: new Date(year, month, i),
        otherMonth: false
      });
    }

    // Dias do próximo mês
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        date: new Date(year, month + 1, i),
        otherMonth: true
      });
    }

    return days;
  };

  const handleDayClick = (date) => {
    onChange?.(date);
    setShowCalendar(false);
  };

  const isDateDisabled = (date) => {
    // diasSemanaPermitidos usa Date#getDay (0 = domingo); vazio/undefined não restringe.
    if (Array.isArray(diasSemanaPermitidos) && diasSemanaPermitidos.length > 0
      && !diasSemanaPermitidos.includes(date.getDay())) {
      return true;
    }
    if (!minDate) return false;
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    const check = new Date(date);
    check.setHours(0, 0, 0, 0);
    return check < min;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!value) return false;
    const selected = new Date(value);
    return date.getDate() === selected.getDate() &&
           date.getMonth() === selected.getMonth() &&
           date.getFullYear() === selected.getFullYear();
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const openCalendar = () => {
    if (disabled) return;

    if (!value) {
      const hoje = new Date();
      setCurrentMonth(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    }

    setShowCalendar(true);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <Container ref={containerRef} className={className} {...props}>
      {customInput ? (
        React.cloneElement(customInput, {
          value: formatDateForDisplay(value),
          onClick: () => {
            if (showCalendar) {
              setShowCalendar(false);
              return;
            }

            openCalendar();
          }
        })
      ) : (
        <>
          <DateInput
            type="text"
            value={formatDateForDisplay(value)}
            placeholder={placeholderText}
            onClick={openCalendar}
            onFocus={openCalendar}
            readOnly
            disabled={disabled}
            aria-label={placeholderText}
          />
          <CalendarIcon
            onClick={() => {
              if (showCalendar) {
                setShowCalendar(false);
                return;
              }

              openCalendar();
            }}
          />
        </>
      )}
      <CalendarPopover $show={showCalendar} $right={popoverRight}>
        <CalendarHeader>
          <NavButton onClick={goToPreviousMonth}>‹</NavButton>
          <MonthYearText>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </MonthYearText>
          <NavButton onClick={goToNextMonth}>›</NavButton>
        </CalendarHeader>
        <WeekDays>
          {weekDays.map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
        </WeekDays>
        <DaysGrid>
          {days.map((dayObj, index) => (
            <DayCell
              key={index}
              onClick={() => handleDayClick(dayObj.date)}
              $selected={isSelected(dayObj.date)}
              $today={isToday(dayObj.date)}
              $otherMonth={dayObj.otherMonth}
              $disabled={isDateDisabled(dayObj.date)}
              disabled={isDateDisabled(dayObj.date)}
            >
              {dayObj.day}
            </DayCell>
          ))}
        </DaysGrid>
      </CalendarPopover>
    </Container>
  );
};

export default ResponsiveDatePicker;
